import ms from 'ms';
import _ from 'lodash';
import pRetry from 'p-retry';
import dayjs from 'dayjs';
import _duration from 'dayjs/plugin/duration';
import * as utils from '../utils';
import * as perf from '../commons/performance-logger';
import * as gridService from '../services/gridService';
import * as concurrencyCompanyService from '../services/concurrencyCompanyService';
import * as testimServicesApi from '../commons/testimServicesApi';
import { getLogger } from '../commons/logger';
import { releasePlayer } from './workerUtils';
import { reporter } from '../reports/reporter';
import { LambdatestService } from '../services/lambdatestService';
import { featureFlagsService } from '../commons/featureFlagsService';
import { remoteStepService } from '../commons/socket/remoteStepService';
import { testResultService } from '../commons/socket/testResultService';
import { getCustomTokenV3 } from '../commons/testimCustomToken';
import { isNetworkHealthy, didNetworkConnectivityTestFail } from '../commons/httpRequest';
import {
    timeoutMessages,
    testRunStatus,
    stepResult,
    runnerTestStatus,
    CLI_MODE,
    APPIUM_SESSION_MESSAGE,
    gridTypes,
} from '../commons/constants';
import {
    StopRunOnError,
    GridError,
    GetBrowserError,
    NotImplementedError,
    PageNotAvailableError,
    GridConcurrencyError,
    InvalidGridCredentials,
} from '../errors';
import type { GridType } from '../commons/constants';
import type { RunnerOptions } from '../runOptions';
import type { TestRunStatus } from '../testRunStatus';
import type { ExecutionQueue } from '../executionQueue';
import type { TestRunHandler } from '../testRunHandler';
import type { IgnoredTestResult, BaseWorkerArgs } from '../runners/WorkerManager';
import type { RawTestResult } from 'services/src/commons/mongo/models/testResult';
import type { SeleniumPerfStatValues } from '../commons/SeleniumPerfStats';
import type { TestResult } from 'clickim/src/common/models/TestResult';

dayjs.extend(_duration);

const logger = getLogger('base-worker');

const { GET_BROWSER_TIMEOUT_MSG, TEST_START_TIMEOUT_MSG, TEST_COMPLETE_TIMEOUT_MSG, TEST_REACHED_CUSTOM_TIMEOUT_MSG } =
    timeoutMessages;
const {
    SETUP_TIMEOUT,
    NETWORK_ERROR,
    GRID_ERROR,
    BROWSER_CLOSED,
    SELENIUM_ERROR,
    UNKNOWN_ERROR,
    MOBILE_SESSION_ERROR,
} = stepResult;

function buildFailureResult(testId: string, testName: string, resultId: string, reason: string) {
    return {
        testId,
        reason,
        name: testName,
        resultId,
        success: false,
    };
}

export type TestPlayer =
    | import('../player/appiumTestPlayer').AppiumTestPlayer
    | import('../player/seleniumTestPlayer').SeleniumTestPlayer
    | import('../player/extensionTestPlayer').ExtensionTestPlayer
    | import('../player/chromeLauncherTestPlayer').ChromeLauncherTestPlayer;

const VERBOSE_TEST_RESULT_PROPERTIES = [
    'fullLogs',
    'locatorStats',
    'stepsResults',
    'setupStepResult',
    'sharedStepClasses',
    'revisionStatus',
    'revisions',
    'testData',
    'exportsGlobal',
] satisfies Array<keyof TestResult | keyof RawTestResult | 'sharedStepClasses'>;

export interface TestResultWithStats extends TestResult, SeleniumPerfStatValues {}

export abstract class BaseWorker<T extends TestPlayer> {
    protected readonly lambdatestService = new LambdatestService();
    protected readonly testRunTimeout: number;
    protected readonly userData: RunnerOptions['userData'];
    protected readonly baseUrl: string | undefined;
    protected testId?: string;
    protected branch?: string;
    protected testName?: string;
    protected testResultId?: string;
    protected overrideTestConfigId?: string;
    protected testRunConfig?: TestRunHandler['runConfig'];
    public id: number = 0;
    public uniqueId: string = '';

    constructor(
        protected readonly executionQueue: ExecutionQueue,
        protected readonly options: RunnerOptions,
        private readonly customExtensionLocalLocation: string | undefined,
        protected readonly executionId: string,
        protected readonly onTestStarted: (
            workerId: number,
            testId: string,
            resultId: string,
            isRerun: boolean | undefined,
            testRetryKey: `${number}:${number}`,
            appSource?: string,
        ) => Promise<any>,
        private readonly onTestCompleted: (
            workerId: number,
            testId: string,
            testResult: any,
            sessionId: string,
            shouldRerun: boolean | undefined,
            appSource?: string,
        ) => Promise<void>,
        private readonly onGridSlot: TestRunStatus['onGridSlot'],
        private readonly onTestIgnored: (workerId: number, testResult: IgnoredTestResult) => void,
        protected readonly releaseSlotOnTestFinished = true,
    ) {
        this.baseUrl = options.baseUrl;
        this.testRunTimeout = options.timeout;
        this.userData = options.userData;
    }

    private async getGridSlot(browser: string, testRunHandler: TestRunHandler) {
        const slot = await gridService.getGridSlot(
            browser,
            testRunHandler.executionId,
            this.options,
            this.id,
            testRunHandler.testResultId,
        );
        this.onGridSlot(testRunHandler.testResultId, slot);
        return slot;
    }

    private async getSlotOnce(testRunHandler: TestRunHandler) {
        const { browserValue } = this.testRunConfig as { browserValue: string };
        reporter.onGetSlot(this.id, browserValue || 'chrome');
        const gridInfo = await this.getGridSlot(browserValue, testRunHandler);
        return gridInfo;
    }

    /** @abstract */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected initPlayer(testRunHandler: TestRunHandler): T {
        throw new NotImplementedError(true);
    }

    /** @abstract */
    protected async getBrowserOnce(
        /* eslint-disable @typescript-eslint/no-unused-vars */
        testRunHandler: TestRunHandler,
        customExtensionLocalLocation: string | undefined,
        player: T,
        gridInfo: Awaited<ReturnType<BaseWorker<T>['getSlotOnce']>>,
        /* eslint-enable @typescript-eslint/no-unused-vars */
    ): Promise<any> {
        throw new NotImplementedError(true);
    }

    protected async runTestOnce(testRunHandler: TestRunHandler, player: T): Promise<any> {
        testRunHandler.sessionId = player.getSessionId();
        logger.info('Test run started', {
            testId: testRunHandler.testId,
            resultId: testRunHandler.testResultId,
            seleniumSession: player.getSessionId(),
            executionId: testRunHandler.executionId,
        });

        return await testRunHandler.clearTestResult();
    }

    protected handleQuarantine(testRunHandler: TestRunHandler) {
        if (!utils.isQuarantineAndNotRemoteRun({ testStatus: testRunHandler.testStatus }, this.options)) {
            return undefined;
        }
        const testResult = {
            name: testRunHandler.testName!,
            testId: testRunHandler.testId,
            resultId: testRunHandler.testResultId,
            runnerStatus: runnerTestStatus.SKIPPED,
            testStatus: testRunHandler.testStatus as 'quarantine',
            testOptimization: testRunHandler.testOptimization,
        };
        this.onTestIgnored(this.id, testResult);
        return testResult;
    }

    protected handleMobileTest(testRunHandler: TestRunHandler) {
        if (this.options.gridData?.mode === 'local') {
            return undefined;
        }
        const isAppFromDevice =
            utils.isAppFromDeviceDisabled(featureFlagsService, this.options.gridData?.type) &&
            testRunHandler.isAppFromDevice &&
            !this.options.appId;
        const isAppForIosVirtualDevice = testRunHandler.isAppForIosVirtualDevice;
        if (isAppFromDevice || isAppForIosVirtualDevice) {
            const testResult: IgnoredTestResult = {
                name: testRunHandler.testName,
                testId: testRunHandler.testId,
                resultId: testRunHandler.testResultId,
                runnerStatus: runnerTestStatus.SKIPPED,
                testStatus: testRunHandler.testStatus,
                mobile: {
                    isAppFromDevice,
                    isAppForIosVirtualDevice,
                },
            };
            this.onTestIgnored(this.id, testResult);
            return testResult;
        }
        return undefined;
    }

    private calculateMobileTestTimeout(gridType: GridType) {
        const increaseTDCRequestTimeout = featureFlagsService.flags.increaseTDCRequestTimeout.isEnabled();
        const requestTimeout = utils.sanitizeNumberValue(
            featureFlagsService.flags.appiumConnectionRequestTimeout.getValue(),
            ms('2m'),
        );
        const retryCount = utils.sanitizeNumberValue(
            featureFlagsService.flags.appiumConnectionRetryCount.getValue(),
            1,
        );
        const timeout = requestTimeout * retryCount;

        if (gridType === gridTypes.BROWSERSTACK || (increaseTDCRequestTimeout && gridType === gridTypes.TESTIM_TDC)) {
            return timeout + ms('10m');
        }
        return timeout + ms('1m');
    }

    private getSessionTimeout() {
        const isMobile = utils.isMobileProject(this.options);
        if (isMobile) {
            const gridType = this.options.gridData!.type!;
            const timeout = this.calculateMobileTestTimeout(gridType);
            return Math.max(timeout, this.options.getSessionTimeout);
        }
        return Math.max(this.lambdatestService.getSessionTimeout || 0, this.options.getSessionTimeout);
    }

    private async getGridInfoAndSlotFromMongoGridQueue(testRunHandler: TestRunHandler) {
        let failedAttemptsCount = 0;
        const gridInfo = await pRetry(
            async () => {
                const startTime = Date.now();
                try {
                    return await utils.promiseTimeout(
                        this.getSlotOnce(testRunHandler),
                        this.options.getBrowserTimeout,
                        timeoutMessages.GET_BROWSER_TIMEOUT_MSG,
                    );
                } catch (error) {
                    failedAttemptsCount++;
                    return await this.handleGetSlotError(error, startTime);
                }
            },
            { retries: this.options.getBrowserRetries - 1, minTimeout: 0, factor: 1 },
        );
        return { gridInfo, failedAttemptsCount };
    }

    private async getSlotFromCompanySlotManager() {
        let failedAttemptsCount = 0;
        const slotId = await pRetry(
            async () => {
                const startTime = Date.now();
                try {
                    const companyId = this.options.company?.companyId;
                    const projectId = this.options.projectData?.projectId;
                    return await utils.promiseTimeout(
                        concurrencyCompanyService.requestSlot(companyId!, projectId!, this.id),
                        this.options.getBrowserTimeout,
                        timeoutMessages.GET_BROWSER_TIMEOUT_MSG,
                    );
                } catch (error) {
                    failedAttemptsCount++;
                    return await this.handleGetSlotError(error, startTime);
                }
            },
            { retries: this.options.getBrowserRetries - 1, minTimeout: 0, factor: 1 },
        );
        return { slotId, failedAttemptsCount };
    }

    private async handleGetSlotError(error: Error, startTime: number) {
        const logDetails = { testId: this.testId, testResultId: this.testResultId, executionId: this.executionId };
        if (error instanceof GridConcurrencyError) {
            logger.info('could not get grid slot due to concurrency issue', logDetails);
        } else {
            logger.error('error getting grid slot', { error, ...logDetails });
        }
        await utils.delay(this.options.getBrowserTimeout - (Date.now() - startTime));
        throw error;
    }

    private async handleGetSlot(testRunHandler: TestRunHandler) {
        if (this.options.slotService === 'redis') {
            const { failedAttemptsCount } = await this.getSlotFromCompanySlotManager();
            return { gridInfo: this.options.gridData, failedAttemptsCount };
        }
        return await this.getGridInfoAndSlotFromMongoGridQueue(testRunHandler);
    }

    protected async getTestPlayer(testRunHandler: TestRunHandler, customExtensionLocalLocation: string | undefined) {
        const projectId = this.userData?.projectId;
        let testPlayer;

        try {
            perf.log('before getSlotOnce retries');
            const { failedAttemptsCount, gridInfo = {} } = await this.handleGetSlot(testRunHandler);
            perf.log('before getBrowserOnce retries');
            const getBrowserRetriesNumber = this.options.getBrowserRetries - failedAttemptsCount;
            if (!getBrowserRetriesNumber) {
                throw new Error('No free browser slots in desired grid');
            }
            let failedGetBrowserAttempts = 0;
            testPlayer = await pRetry(
                async () => {
                    const startTime = Date.now();
                    const player = this.initPlayer(testRunHandler);
                    try {
                        const updatedGridInfo = await gridService.handleHybridOrVendorIfNeeded(
                            this.options,
                            gridInfo,
                            this.testRunConfig,
                            this.lambdatestService,
                            { maxRetries: getBrowserRetriesNumber, currentRetry: failedGetBrowserAttempts + 1 },
                        );
                        this.options.gridData!.provider = updatedGridInfo.provider;
                        this.options.gridData!.host = updatedGridInfo.host;
                        this.options.gridData!.failedGetBrowserAttempts = failedGetBrowserAttempts;
                        perf.log('before getBrowserOnce');
                        const getBrowserRes = await utils.promiseTimeout(
                            this.getBrowserOnce(testRunHandler, customExtensionLocalLocation, player, updatedGridInfo),
                            this.getSessionTimeout(),
                            timeoutMessages.GET_BROWSER_TIMEOUT_MSG,
                        );
                        perf.log('after getBrowserOnce');
                        reporter.onGetBrowserSuccess(this.id, projectId!);
                        return player || getBrowserRes;
                    } catch (error) {
                        const grid = { ...gridInfo, failedGetBrowserAttempts, id: this.options.gridData!.gridId };
                        const instanceType = this.options.mode === CLI_MODE.APPIUM ? 'device' : 'browser';
                        logger.error(`error getting ${instanceType} from grid`, {
                            error,
                            testId: this.testId,
                            testResultId: this.testResultId,
                            executionId: this.executionId,
                            grid,
                            publicIps: this.options.publicIps,
                        });
                        reporter.onGetBrowserFailure(this.id, projectId!, ++failedGetBrowserAttempts);
                        player.onDone();
                        if (!(error instanceof PageNotAvailableError) && this.options.mode !== CLI_MODE.APPIUM) {
                            await utils.delay(this.options.getBrowserTimeout - (Date.now() - startTime));
                        }
                        // Note - some errors extends pRetry native abort error, in these cases pRetry will break the retry loop and won't continue to try to get a browser.
                        throw error;
                    }
                },
                { retries: getBrowserRetriesNumber - 1, minTimeout: 0, factor: 1 },
            );
            perf.log('after getBrowserOnce retries');
        } catch (err) {
            await releasePlayer(
                this.id,
                this.releaseSlotOnTestFinished,
                projectId!,
                this.options.company!.companyId,
                this.options.slotService,
                testPlayer,
            );
            if ([PageNotAvailableError, InvalidGridCredentials].some((errCls) => err instanceof errCls)) {
                throw err;
            }

            if (err instanceof GridError) {
                throw new GetBrowserError(err, GRID_ERROR);
            }
            throw new GetBrowserError(err, SELENIUM_ERROR);
        }

        return testPlayer;
    }

    protected async runTest(
        testRunHandler: TestRunHandler,
        customExtensionLocalLocation: string | undefined,
        shouldRerun: boolean | undefined,
    ) {
        perf.log('inside runTest');
        const projectId = this.userData?.projectId;
        const quarantineResult = this.handleQuarantine(testRunHandler);
        if (quarantineResult) {
            return quarantineResult;
        }

        const mobileTestResult = utils.isMobileProject(this.options) && this.handleMobileTest(testRunHandler);
        if (mobileTestResult) {
            return mobileTestResult;
        }

        perf.log('before runTest onTestStarted');
        const appSource = testRunHandler.getAppSource;
        const test = await this.onTestStarted(
            this.id,
            testRunHandler.testId,
            testRunHandler.testResultId,
            shouldRerun,
            testRunHandler.retryKey,
            appSource,
        );
        testRunHandler.baseUrl = test.config.baseUrl;

        const testPlayer = await this.getTestPlayer(testRunHandler, customExtensionLocalLocation);

        let ltData = this.lambdatestService.smartUIIntegrationData;
        if (!ltData?.projectToken && projectId) {
            try {
                const testimServicesApi = await import('../commons/testimServicesApi');
                ltData = await testimServicesApi.getVisualTestingIntegrationData(projectId, 'lambdatest');
            } catch (e) {
                // SmartUI integration data not available
            }
        }
        if (ltData?.username && ltData?.accessKey && ltData?.projectToken) {
            testRunHandler.smartUIIntegrationData = ltData as import('clickim/src/common/api/testimVisualTestingApi').LambdaTestIntegrationData;
        }

        try {
            // We want to handle token refresh in the runner to make sure we are not starting tests with invalid tokens.
            this.userData!.loginData!.token = await getCustomTokenV3();
            return await this.runTestOnce(testRunHandler, testPlayer);
        } finally {
            await releasePlayer(
                this.id,
                this.releaseSlotOnTestFinished,
                projectId!,
                this.options.company!.companyId,
                this.options.slotService,
                testPlayer,
            );
        }
    }

    protected async runTestCleanup(): Promise<void> {
        return undefined;
    }

    protected async onQueueCompleted(): Promise<void> {
        return undefined;
    }

    public run(
        testRunHandler: TestRunHandler,
        onWorkerFinished: (workerId: number, ...args: BaseWorkerArgs) => void,
        ...args: BaseWorkerArgs
    ) {
        interface RunCompletionData {
            testId?: string;
            reason?: string;
            name?: string;
            resultId?: string;
            success?: boolean;
            testStatus?: string;
            testRetryKey?: string;
        }
        const onRunComplete = async (testResult: RunCompletionData, err?: Error) => {
            if (
                utils.isQuarantineAndNotRemoteRun(testResult, this.options) ||
                (utils.isAppFromDeviceDisabled(featureFlagsService, this.options.gridData?.type) &&
                    testRunHandler.isAppFromDevice &&
                    !this.options.appId) ||
                testRunHandler.isAppForIosVirtualDevice
            ) {
                onWorkerFinished(this.id, ...args);
                return undefined;
            }
            const sessionId = testRunHandler.sessionId!;
            testRunHandler.setReRunOnTestStartTimeOutError(false);

            const isTimeoutError = (timeoutMsg: string) => err?.message.includes(timeoutMsg);
            const isIgnoreErrors = err && err instanceof GetBrowserError;
            const isTestStartedTimeout = isTimeoutError(TEST_START_TIMEOUT_MSG);
            const isTimeoutErrors =
                err &&
                (isTimeoutError(TEST_COMPLETE_TIMEOUT_MSG) ||
                    isTimeoutError(TEST_REACHED_CUSTOM_TIMEOUT_MSG) ||
                    (isTestStartedTimeout && !testRunHandler.retryOnInfraErrors));
            const didTimeoutWithCustomTimeout =
                !!isTimeoutError(TEST_REACHED_CUSTOM_TIMEOUT_MSG) && !!this.testRunTimeout;
            const isSeleniumHubError =
                !!testResult.reason && testRunHandler.isSeleniumHubNoMatchSessionError(testResult.reason);

            const shouldRerunOnInfraError =
                (isSeleniumHubError || isTestStartedTimeout) && testRunHandler.retryOnInfraErrors;
            const shouldRerunOnTimeoutError =
                isTimeoutErrors && testRunHandler.hasMoreTimeoutRetries() && !didTimeoutWithCustomTimeout;
            const shouldRenRunOnMoreRetries = testRunHandler.hasMoreRetries() && !isIgnoreErrors && !isTimeoutErrors;
            const shouldRerun =
                !testResult.success &&
                (shouldRenRunOnMoreRetries || shouldRerunOnTimeoutError || shouldRerunOnInfraError);

            try {
                const testRetryKey = testRunHandler.retryKey;
                testResult.testRetryKey = testRetryKey;
                const appSource = testRunHandler.getAppSource;
                await this.onTestCompleted(this.id, this.testId!, testResult, sessionId, shouldRerun, appSource);
                await this.runTestCleanup();
                if (shouldRerun) {
                    if (shouldRerunOnTimeoutError) {
                        await testRunHandler.startNewTimeoutRetry();
                    }
                    if (shouldRerunOnInfraError) {
                        logger.info(
                            `retry test id: ${this.testId} name: ${this.testName} again due to infrastructure issues`,
                            {
                                testId: testRunHandler.testId,
                                retryReason: testResult.reason,
                                testName: this.testName,
                                testResultId: this.testResultId,
                                executionId: this.executionId,
                                err,
                            },
                        );
                        const projectId = this.options.projectData?.projectId;
                        const testResultId = testRunHandler.testResultId;
                        const testId = testRunHandler.testId;
                        const deleteTestResult = { show: false };
                        await testimServicesApi.updateTestResult(projectId!, testResultId, testId, deleteTestResult);
                        testRunHandler.decreaseInfraErrorRetry();
                        testRunHandler.decreaseRetryCount();
                        testRunHandler.setReRunOnTestStartTimeOutError(true);
                        await testRunHandler.startNewRetry();
                    }
                    if (shouldRenRunOnMoreRetries && !shouldRerunOnInfraError && !shouldRerunOnTimeoutError) {
                        await testRunHandler.startNewRetry();
                    }
                    logger.info(`retry test id: ${this.testId} name: ${this.testName} again`, {
                        testId: this.testId,
                        testName: this.testName,
                        isTimeoutErrors,
                        testRetryKey,
                        totalRetries: testRunHandler._totalRetryCount,
                    });
                    this.testResultId = testRunHandler.testResultId;
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    return await runTestAndCalcResult(shouldRerun);
                }
                onWorkerFinished(this.id, ...args);
                return undefined;
            } catch (error) {
                if (error instanceof StopRunOnError) {
                    return undefined;
                }
                logger.error('failed to process test result', { error });
                onWorkerFinished(this.id, ...args);
                return undefined;
            }
        };
        const getNetworkErrorMessage = () =>
            'Due to network connectivity issues, Testim CLI has been unable to connect to the grid.\n' +
            `Please make sure the CLI has stable access to the internet. ${didNetworkConnectivityTestFail() ? '(Internal: network connectivity test failed)' : ''}`;

        const buildMobileSessionError = (err: Error) => {
            const {
                DEVICE_OS_VERSION_TOO_LOW,
                APP_NOT_EXIST_ON_DEVICE,
                APP_NEVER_STARTED,
                TIMEOUT_ERROR,
                SAUCELABS_APP_IS_NOT_INSTALLED,
            } = APPIUM_SESSION_MESSAGE;

            const errorMessage = err instanceof Error ? err.message : err;
            // Cutting the error message to the first 1000 characters as huge messages can cause Kafka -> Mongo issues - we don't need the full message anyway.
            const msg = errorMessage.slice(0, 1000);
            msg.replace(/(Visit\s+)?(https?:\/\/\S+)?(\s+)?(for\s+troubleshooting(\.)?)?/gi, '');
            //TODO: map more error messages
            if (msg.includes(GET_BROWSER_TIMEOUT_MSG) || msg.includes(TIMEOUT_ERROR)) {
                return { errorType: SETUP_TIMEOUT, reason: 'timeout while getting device' };
            }
            if (msg.includes(TEST_COMPLETE_TIMEOUT_MSG) || msg.includes(TEST_REACHED_CUSTOM_TIMEOUT_MSG)) {
                if (!this.testRunTimeout) {
                    return { errorType: SETUP_TIMEOUT, reason: 'Test timeout reached!' };
                }
                const duration = dayjs.duration({ milliseconds: this.testRunTimeout });
                return {
                    errorType: SETUP_TIMEOUT,
                    reason: `Test timeout reached (timeout: ${utils.formatTimeoutDuration(duration)})`,
                };
            }
            if (msg.includes(APP_NOT_EXIST_ON_DEVICE) || msg.includes(SAUCELABS_APP_IS_NOT_INSTALLED)) {
                return { errorType: MOBILE_SESSION_ERROR, reason: "app doesn't exist on device" };
            }
            if (msg.includes(APP_NEVER_STARTED)) {
                return {
                    errorType: MOBILE_SESSION_ERROR,
                    reason: 'app never started, check app permission or app activity',
                };
            }
            if (msg.includes(DEVICE_OS_VERSION_TOO_LOW)) {
                return { errorType: MOBILE_SESSION_ERROR, reason: 'Unable to install app: DeviceOSVersionTooLow' };
            }
            return { errorType: MOBILE_SESSION_ERROR, reason: msg };
        };

        const buildError = (err: Error, wasNetworkHealthy: boolean) => {
            const instanceType = this.options.mode === CLI_MODE.APPIUM ? 'device' : 'browser';
            if (!wasNetworkHealthy && featureFlagsService.flags.errorMessageOnBadNetwork.isEnabled()) {
                return {
                    errorType: NETWORK_ERROR,
                    reason: getNetworkErrorMessage(),
                };
            }
            const errorMessage = err instanceof Error ? err.message : err;
            // Cutting the error message to the first 1000 characters as huge messages can cause Kafka -> Mongo issues - we don't need the full message anyway.
            const msg = errorMessage.slice(0, 1000);
            if (msg.includes(GET_BROWSER_TIMEOUT_MSG)) {
                return { errorType: SETUP_TIMEOUT, reason: `Test couldn't get ${instanceType}` };
            }
            if (msg.includes(TEST_START_TIMEOUT_MSG)) {
                return { errorType: SETUP_TIMEOUT, reason: "Test couldn't be started" };
            }
            if (err instanceof GetBrowserError && err.type) {
                if (err.type === GRID_ERROR) {
                    return {
                        errorType: GRID_ERROR,
                        reason: `Test couldn't get ${instanceType} from grid - ${msg}`,
                    };
                }
                if (err.type === SELENIUM_ERROR) {
                    return { errorType: SELENIUM_ERROR, reason: `Failed to create new session - ${msg}` };
                }
            }
            if (msg.includes(TEST_COMPLETE_TIMEOUT_MSG)) {
                if (!this.testRunTimeout) {
                    return { errorType: SETUP_TIMEOUT, reason: 'Test timeout reached: test is too long' };
                }

                return {
                    errorType: SETUP_TIMEOUT,
                    reason: `Test timeout reached (timeout: ${utils.formatTimeoutDuration(dayjs.duration({ milliseconds: this.testRunTimeout }))})`,
                };
            }
            if ('type' in err && err.type === BROWSER_CLOSED) {
                return {
                    errorType: SELENIUM_ERROR,
                    reason: `Session terminated, this is often caused to connection
                                problems between the Testim CLI and the grid running the test. Please check your network connection.`,
                };
            }
            if ('type' in err && err.type === 'KEEP_ALIVE_FAILED') {
                return {
                    errorType: SELENIUM_ERROR,
                    reason: 'Test terminated due to repeated keep alive failures. The browser may have become unresponsive or the test environment may be unstable.',
                };
            }
            if (
                /SeleniumError: connect ECONNREFUSED/.test(err.message) ||
                /Couldn't connect to selenium server/.test(err.message)
            ) {
                return {
                    errorType: SELENIUM_ERROR,
                    reason: 'Failed to connect to the grid, please check if the grid is accessible from your network',
                };
            }
            if (/terminated due to FORWARDING_TO_NODE_FAILED/.test(err.message)) {
                return {
                    errorType: SELENIUM_ERROR,
                    reason: 'Session terminated, it is likely that the grid is out of memory or not responding, please try to rerun the test',
                };
            }
            if (/terminated due to PROXY_REREGISTRATION/.test(err.message)) {
                return {
                    errorType: SELENIUM_ERROR,
                    reason: 'Session terminated, it is likely that the grid is not responding, please try to rerun the test',
                };
            }
            if (/forwarding the new session cannot find : Capabilities/.test(err.message)) {
                return {
                    errorType: SELENIUM_ERROR,
                    reason: `Session could not be created, please check that the ${instanceType} you requested is supported in your plan`,
                };
            }
            return { errorType: UNKNOWN_ERROR, reason: msg };
        };

        const onRunError = async (err: Error) => {
            const wasNetworkHealthy = await isNetworkHealthy();
            if (!wasNetworkHealthy && featureFlagsService.flags.warnOnBadNetwork.isEnabled()) {
                // intentional, we want to log to stderr:
                // eslint-disable-next-line no-console
                console.warn(getNetworkErrorMessage());
            }
            logger.warn('error on run', { err });

            const projectId = this.userData?.projectId;
            const { errorType, reason } =
                this.options.mode === CLI_MODE.APPIUM
                    ? buildMobileSessionError(err)
                    : buildError(err, wasNetworkHealthy);
            testimServicesApi.updateTestResult(
                projectId!,
                this.testResultId!,
                this.testId!,
                {
                    status: testRunStatus.COMPLETED,
                    success: false,
                    reason,
                    errorType,
                    testRetryKey: testRunHandler.retryKey,
                    setupStepResult: { status: testRunStatus.COMPLETED, success: false, reason, errorType },
                },
                testRunHandler.remoteRunId,
            ).catch(error => logger.error('Failed to update test result after test failed', { error }));
            await onRunComplete(buildFailureResult(this.testId!, this.testName!, this.testResultId!, reason), err);
        };

        const recoverTestResults = async (runError: Error) => {
            const testId = this.testId;
            const resultId = this.testResultId;
            const projectId = this.userData?.projectId;
            const branch = this.branch;
            if (!testId || !resultId || !projectId || !branch) {
                // Not enough data to call the API
                logger.warn('Test failed. Not enough data to recover results via API', { err: runError });
                return onRunError(runError);
            }

            try {
                const testResult = await testimServicesApi.getTestResults(testId, resultId, projectId, branch);
                logger.warn('Test failed. Got results via API', {
                    err: runError,
                    testResult: _.omit(testResult, VERBOSE_TEST_RESULT_PROPERTIES),
                    resultId,
                });
                if (
                    testResult &&
                    testResult.status === testRunStatus.COMPLETED &&
                    !runError.message.includes(TEST_COMPLETE_TIMEOUT_MSG)
                ) {
                    return await onRunComplete(testResult);
                }
                throw runError;
            } catch (err) {
                if (err !== runError) {
                    logger.error('Failed to fetch test results from server', {
                        testId,
                        resultId,
                        projectId,
                        branch,
                        err,
                    });
                }
                return onRunError(runError);
            }
        };

        const disableResults =
            this.options.disableSockets ||
            (this.options.lightweightMode?.disableResults &&
                (this.options.useChromeLauncher || this.options.mode === CLI_MODE.SELENIUM));
        const disableRemoteStep = this.options.disableSockets || this.options.lightweightMode?.disableRemoteStep;

        const runTestAndCalcResult = async (shouldRerun?: boolean): Promise<any> => {
            try {
                await Promise.all([
                    !disableRemoteStep && remoteStepService.joinToRemoteStep(this.testResultId!),
                    !disableResults && testResultService.joinToTestResult(this.testResultId!, this.testId!),
                ]);
                if (this.options.mode !== CLI_MODE.APPIUM) {
                    testRunHandler.validateRunConfig();
                }
                const testResult = await this.runTest(testRunHandler, this.customExtensionLocalLocation, shouldRerun);
                await onRunComplete(testResult);
                perf.log('After onRunComplete');
                onWorkerFinished(this.id, ...args);
            } catch (runError) {
                await recoverTestResults(runError);
                onWorkerFinished(this.id, ...args);
            } finally {
                if (!disableRemoteStep) {
                    remoteStepService.unlistenToRemoteStep(this.testResultId!);
                }
            }
        };

        this.testId = testRunHandler.testId;
        this.testName = testRunHandler.testName;
        this.testResultId = testRunHandler.testResultId;
        this.overrideTestConfigId = testRunHandler.overrideTestConfigId;
        this.testRunConfig = testRunHandler.runConfig;
        this.branch = testRunHandler.branch;

        return runTestAndCalcResult();
    }
}
